import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import { accessLocalStorage, exportSelectAllQueues, getIntegrationsByAccountcode } from '../../../services';
import { getMessageConfigById, updateMessageConfig, validateMessageConfig } from '../../../services/messageApi';
import { getContextsByTenant } from '../../../services/contextsApi';
import { getAvailableTrunks } from '../../../services/incomingCallsApi';

import './MessageForm.css';
import Loading from '../../../components/Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEnvelope, 
  faSave, 
  faTimes,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';

import AudioConfiguration from './components/AudioConfiguration';
import DestinationConfiguration from './components/DestinationConfiguration';
import TimeConditionManager from './components/TimeConditionManager';
import OverflowManager from './components/OverflowManager';
import MohConfiguration from '../shared/MohConfiguration';

function MessageEdit() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userTenant, setUserTenant] = useState('');
  const [userRole, setUserRole] = useState('');
  const [availableQueues, setAvailableQueues] = useState([]);
  const [availableContexts, setAvailableContexts] = useState([]);
  const [availableTrunks, setAvailableTrunks] = useState([]);
  const [hasWhatsAppIntegration, setHasWhatsAppIntegration] = useState(false);

  // Alert state
  const [alert, setAlert] = useState({
    show: false,
    type: '',
    message: ''
  });

  const [values, setValues] = useState({
    tenant: '',
    context_name: '',
    description: '',
    audio_enabled: false,
    audio_file: '',
    audio_type: 'message',
    audio_accountcode: '',
    destination_type: 'endpoint',
    destination: '',
    trunk_name: '',
    timeout_main: '',
    stasis_fallback_type: '',
    stasis_fallback_destination: '',
    moh_enabled: false,
    moh_class: '',
    gotoiftime_rules: [],
    overflows: []
  });

  const history = useHistory();
  const { id } = useParams();

  useEffect(() => {
    loadConfigData();
  }, [id]);

  const loadConfigData = async () => {
    try {
      const user = await accessLocalStorage.getUserLocalStorage();
      if (!user?.user?.idtenant) {
        history.push('/');
        return;
      }

      const tenant = user.user.idtenant;
      setUserTenant(tenant);
      setUserRole(user.user.role || '');

      console.log('🔍 [MESSAGE-EDIT] Carregando configuração ID:', id);

      // Buscar configuração existente
      const response = await getMessageConfigById(id);
      
      if (!response || !response.success) {
        alert('Erro ao carregar configuração');
        history.push('/config/message');
        return;
      }

      const config = response.data;
      
      let processedDestination = config.destination || '';
      let processedTrunkName = '';
      
      if (config.destination_type === 'external' && config.destination?.includes('@')) {
        const [number, trunk] = config.destination.split('@');
        processedDestination = number;
        processedTrunkName = trunk;
      }
      
      const processedOverflows = (config.overflows || []).map(overflow => {
        if (overflow.destination_type === 'external' && overflow.destination_overflow?.includes('@')) {
          const [number, trunk] = overflow.destination_overflow.split('@');
          return {
            ...overflow,
            destination_overflow: number,
            trunk_name: trunk
          };
        }
        return overflow;
      });
      
      setValues({
        tenant: config.tenant,
        context_name: config.context_name,
        description: config.description || '',
        audio_enabled: config.audio_enabled || false,
        audio_file: config.audio_file || '',
        audio_type: config.audio_type || 'message',
        audio_accountcode: config.audio_accountcode || tenant,
        destination_type: config.destination_type || 'endpoint',
        destination: processedDestination,
        trunk_name: processedTrunkName,
        timeout_main: (config.timeout_main !== null && config.timeout_main !== undefined) ? config.timeout_main : '',
        stasis_fallback_type: config.stasis_fallback_type || '',
        stasis_fallback_destination: config.stasis_fallback_destination || '',
        moh_enabled: config.moh_enabled || false,
        moh_class: config.moh_class || '',
        gotoiftime_rules: config.gotoiftime_rules || [],
        overflows: processedOverflows
      });

      console.log('✅ [MESSAGE-EDIT] Configuração carregada:', config);

      // Buscar filas disponíveis
      try {
        console.log('🔍 [MESSAGE-EDIT] Buscando filas...');
        const queuesData = await exportSelectAllQueues();
        
        let queues = [];
        if (Array.isArray(queuesData)) {
          queues = queuesData;
        } else if (queuesData && Array.isArray(queuesData.data)) {
          queues = queuesData.data;
        } else if (queuesData && queuesData.queues) {
          queues = queuesData.queues;
        }
        
        // Filtrar apenas filas do tenant atual
        const tenantQueues = tenant.includes('vgs') 
          ? queues 
          : queues.filter(q => {
              const accountcode = q.accountcodequeue || q.accountcode;
              return tenant.includes(accountcode);
            });
        
        setAvailableQueues(tenantQueues);
        console.log(`✅ [MESSAGE-EDIT] ${tenantQueues.length} filas carregadas`);
      } catch (error) {
        console.error('⚠️ [MESSAGE-EDIT] Erro ao buscar filas:', error);
      }
      
      // Buscar contextos disponíveis (para DISA)
      try {
        console.log('🔍 [MESSAGE-EDIT] Buscando contextos...');
        const contextsData = await getContextsByTenant(tenant);
        
        let contexts = [];
        if (Array.isArray(contextsData)) {
          contexts = contextsData;
        } else if (contextsData && Array.isArray(contextsData.data)) {
          contexts = contextsData.data;
        } else if (contextsData && contextsData.contexts) {
          contexts = contextsData.contexts;
        }
        
        setAvailableContexts(contexts);
        console.log(`✅ [MESSAGE-EDIT] ${contexts.length} contextos carregados`);
      } catch (error) {
        console.error('⚠️ [MESSAGE-EDIT] Erro ao buscar contextos:', error);
      }
      
      try {
        console.log('🔍 [MESSAGE-EDIT] Buscando troncos...');
        const trunksResponse = await getAvailableTrunks(tenant);
        
        if (trunksResponse && trunksResponse.success && Array.isArray(trunksResponse.data)) {
          const filteredTrunks = trunksResponse.data.filter(trunk => 
            trunk.accountcode === tenant || trunk.tenant === tenant
          );
          setAvailableTrunks(filteredTrunks);
          console.log(`✅ [MESSAGE-EDIT] ${filteredTrunks.length} troncos carregados`);
        } else {
          setAvailableTrunks([]);
          console.log('⚠️ [MESSAGE-EDIT] Nenhum tronco encontrado');
        }
      } catch (error) {
        console.error('⚠️ [MESSAGE-EDIT] Erro ao buscar troncos:', error);
        setAvailableTrunks([]);
      }
      
      await checkWhatsAppIntegration(tenant);
      
      setLoading(false);
    } catch (error) {
      console.error('❌ [MESSAGE-EDIT] Erro ao carregar dados:', error);
      alert('Erro ao carregar configuração');
      history.push('/config/message');
    }
  };

  const checkWhatsAppIntegration = async (tenant) => {
    try {
      console.log('🔍 [MESSAGE-EDIT] Verificando integrações WhatsApp para tenant:', tenant);
      const response = await getIntegrationsByAccountcode(tenant);
      if (response && response.success && Array.isArray(response.data) && response.data.length > 0) {
        setHasWhatsAppIntegration(true);
        console.log('✅ [MESSAGE-EDIT] Integrações WhatsApp encontradas:', response.data.length);
      } else {
        setHasWhatsAppIntegration(false);
        console.log('⚠️ [MESSAGE-EDIT] Nenhuma integração WhatsApp encontrada');
      }
    } catch (error) {
      console.error('❌ [MESSAGE-EDIT] Erro ao verificar integrações WhatsApp:', error);
      setHasWhatsAppIntegration(false);
    }
  };

  const handleChange = (field, value) => {
    setValues(prev => ({ ...prev, [field]: value }));
  };

  const handleAudioConfigChange = (audioConfig) => {
    setValues(prev => ({
      ...prev,
      audio_enabled: audioConfig.audio_enabled,
      audio_file: audioConfig.audio_file,
      audio_type: audioConfig.audio_type,
      audio_accountcode: audioConfig.audio_accountcode
    }));
  };

  const handleDestinationConfigChange = (destConfig) => {
    const isStasis = destConfig.destination_type === 'stasis';
    setValues(prev => ({
      ...prev,
      destination_type: destConfig.destination_type,
      destination: destConfig.destination,
      trunk_name: destConfig.trunk_name,
      timeout_main: (destConfig.timeout_main !== undefined) ? destConfig.timeout_main : prev.timeout_main,
      stasis_fallback_type: isStasis ? (destConfig.stasis_fallback_type || '') : '',
      stasis_fallback_destination: isStasis ? (destConfig.stasis_fallback_destination || '') : ''
    }));
  };

  const handleTimeConditionsChange = (rules) => {
    setValues(prev => ({
      ...prev,
      gotoiftime_rules: rules
    }));
  };

  const handleOverflowsChange = (overflows) => {
    setValues(prev => ({
      ...prev,
      overflows: overflows
    }));
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    // Scroll para o topo da página para mostrar o alert
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 5000);
  };

  const processDataForSave = (data) => {
    const processed = { ...data };
    
    if (processed.destination_type === 'external' && processed.trunk_name && processed.destination) {
      processed.destination = `${processed.destination}@${processed.trunk_name}`;
    }
    
    if (processed.overflows && processed.overflows.length > 0) {
      processed.overflows = processed.overflows.map(overflow => {
        if (overflow.destination_type === 'external' && overflow.trunk_name && overflow.destination_overflow) {
          return {
            ...overflow,
            destination_overflow: `${overflow.destination_overflow}@${overflow.trunk_name}`
          };
        }
        return overflow;
      });
    }
    
    return processed;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateMessageConfig(values);
    if (!validation.valid) {
      const errorMessage = validation.errors.join('\n');
      showAlert('danger', `Erro de validação:\n\n${errorMessage}`);
      return;
    }
    
    setSaving(true);
    
    try {
      const dataToSave = processDataForSave(values);
      console.log('🔍 [MESSAGE-EDIT] Enviando dados:', dataToSave);
      
      const response = await updateMessageConfig(id, dataToSave);
      
      if (response && response.success) {
        showAlert('success', 'Contexto de mensagem atualizado com sucesso!');
        setTimeout(() => {
          history.push('/config/message');
        }, 2000);
      } else {
        const errorMsg = response.error || response.message || 'Erro desconhecido ao atualizar configuração';
        showAlert('danger', `Erro ao atualizar configuração:\n\n${errorMsg}`);
      }
    } catch (error) {
      console.error('❌ [MESSAGE-EDIT] Erro ao salvar:', error);
      showAlert('danger', 'Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="message-form-container">
      <div className="message-form-header">
        <div className="message-form-title-wrapper">
          <svg className="message-form-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <div>
            <h1 className="message-form-title">Editar Contexto: {values.context_name}</h1>
            <p className="message-form-subtitle">Modifique a configuração do contexto de mensagem</p>
          </div>
        </div>
      </div>

      {alert.show && (
        <div className="alerts-section">
          <div className={`alert-message alert-${alert.type}`}>
            <div className="alert-header">
              <span>{alert.type === 'success' ? 'Sucesso!' : 'Erro!'}</span>
              <button className="alert-close" onClick={() => setAlert({ ...alert, show: false })}>×</button>
            </div>
            <div className="alert-body">{alert.message}</div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-card">
          <div className="form-section-title">
            <FontAwesomeIcon icon={faInfoCircle} />
            Informações Básicas
          </div>

          <div className="form-field">
            <label className="form-label">Nome do Contexto</label>
            <div className="form-control">
              <input
                className="form-input"
                type="text"
                value={values.context_name}
                disabled
              />
            </div>
            <p className="form-help">
              O nome do contexto não pode ser alterado após a criação
            </p>
          </div>

          <div className="form-field">
            <label className="form-label">Descrição</label>
            <div className="form-control">
              <textarea
                className="form-textarea"
                placeholder="Descrição opcional do contexto de mensagem"
                value={values.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows="3"
              />
            </div>
            <p className="form-help">Adicione uma descrição para facilitar a identificação</p>
          </div>
        </div>

        <AudioConfiguration
          config={{
            audio_enabled: values.audio_enabled,
            audio_file: values.audio_file,
            audio_type: values.audio_type,
            audio_accountcode: values.audio_accountcode
          }}
          onChange={handleAudioConfigChange}
          tenant={userTenant}
          title="Áudio do Contexto"
        />

        {/* Configuração de Music on Hold */}
        <div className="form-card">
          <MohConfiguration
            config={{
              moh_enabled: values.moh_enabled || false,
              moh_class: values.moh_class || ''
            }}
            tenant={userTenant}
            onChange={(mohConfig) => {
              setValues(prev => ({
                ...prev,
                moh_enabled: mohConfig.moh_enabled,
                moh_class: mohConfig.moh_class
              }));
            }}
            disabled={saving}
          />
        </div>

        <TimeConditionManager
          rules={values.gotoiftime_rules}
          onChange={handleTimeConditionsChange}
          availableQueues={availableQueues}
          availableContexts={availableContexts}
          hasWhatsAppIntegration={hasWhatsAppIntegration}
        />

        <DestinationConfiguration
          config={{
            destination_type: values.destination_type,
            destination: values.destination,
            trunk_name: values.trunk_name,
            timeout_main: values.timeout_main,
            stasis_fallback_type: values.stasis_fallback_type,
            stasis_fallback_destination: values.stasis_fallback_destination
          }}
          onChange={handleDestinationConfigChange}
          availableQueues={availableQueues}
          availableContexts={availableContexts}
          availableTrunks={availableTrunks}
          title="Destino Principal"
          showCycleOption={false}
          hasWhatsAppIntegration={hasWhatsAppIntegration}
          userRole={userRole}
          hasOverflows={(values.overflows || []).length > 0}
        />

        <OverflowManager
          overflows={values.overflows}
          onChange={handleOverflowsChange}
          availableQueues={availableQueues}
          availableContexts={availableContexts}
          availableTrunks={availableTrunks}
          tenant={userTenant}
          hasWhatsAppIntegration={hasWhatsAppIntegration}
          userRole={userRole}
        />

        <div className="form-card">
          <div className="form-actions">
            <Link to="/config/message" className="btn-cancel">
              <FontAwesomeIcon icon={faTimes} />
              Cancelar
            </Link>
            <button 
              type="submit"
              className={`btn-submit ${saving ? 'is-loading' : ''}`}
              disabled={saving}
            >
              <FontAwesomeIcon icon={faSave} />
              Salvar Configuração
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default MessageEdit;

